import { useMemo } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ColumnSelectRole = 'x' | 'y' | 'series' | 'facet';

export interface ColumnSelectProps {
  role: ColumnSelectRole;
  columns: string[];
  recommendedTop: string[];
  value: string[] | string | null;
  onChange: (value: string[] | string | null, source: 'top' | 'all') => void;
  multiple?: boolean;
}

export function ColumnSelect({ role, columns, recommendedTop, value, onChange, multiple }: ColumnSelectProps) {
  const selectedSet = useMemo(() => {
    if (Array.isArray(value)) return new Set(value);
    if (typeof value === 'string' && value) return new Set([value]);
    return new Set<string>();
  }, [value]);

  const displayText = useMemo(() => {
    const vals = Array.isArray(value) ? value : (value ? [value] : []);
    if (vals.length === 0) return 'Pick column(s)';
    return vals.join(', ');
  }, [value]);

  const allAlpha = useMemo(() => {
    const dedup = new Set<string>();
    const merged: string[] = [];
    // Put recommended first
    for (const n of recommendedTop) {
      if (!dedup.has(n)) { dedup.add(n); merged.push(n); }
    }
    // Then the rest in alpha order, skipping any already included
    const rest = [...columns].sort((a, b) => a.localeCompare(b));
    for (const n of rest) {
      if (!dedup.has(n)) { dedup.add(n); merged.push(n); }
    }
    return merged;
  }, [columns, recommendedTop]);

  const handlePick = (name: string, source: 'top' | 'all') => {
    if (multiple) {
      const next = new Set(selectedSet);
      if (next.has(name)) next.delete(name); else next.add(name);
      onChange(Array.from(next), source);
    } else {
      onChange(name, source);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between h-8">
          <span className="truncate text-left text-xs">{displayText}</span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]">
        <Command>
          <CommandInput placeholder="Search columns" className="h-8" />
          <CommandList>
            <CommandEmpty>No columns found</CommandEmpty>
            <CommandGroup heading="">
              {allAlpha.map((name, idx) => (
                <CommandItem key={`opt-${name}`} value={name} onSelect={() => handlePick(name, idx < recommendedTop.length ? 'top' : 'all')}>
                  <Check className={cn('mr-2 h-3 w-3', selectedSet.has(name) ? 'opacity-100' : 'opacity-0')} />
                  <span className="text-xs">{name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ColumnSelect;


